<template>
  <div class="container-fluid py-3">
    <div class="card shadow-sm mx-auto" style="max-width: 700px;">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          {{ analisisLocal?.id ? '✏️ Editar Análisis' : '🧪 Nuevo Análisis de Calidad' }}
        </h5>
      </div>

      <div class="card-body">
        <form @submit.prevent="submitForm" novalidate>
          <div class="row g-3">
            <!-- Tipo de Análisis -->
            <div class="col-12 col-md-6">
              <label class="form-label" for="tipoAnalisis">Tipo de Análisis</label>
              <select
                id="tipoAnalisis"
                class="form-select"
                v-model="analisisLocal.tipo_analisis_id"
                :class="{'is-invalid': error && !analisisLocal.tipo_analisis_id}"
                required
              >
                <option disabled value="">Seleccione tipo...</option>
                <option v-for="tipo in tiposAnalisis" :key="tipo.id" :value="tipo.id">
                  {{ tipo.nombre }}
                </option>
              </select>
              <div v-if="error && !analisisLocal.tipo_analisis_id" class="invalid-feedback">
                Debe seleccionar un tipo de análisis
              </div>
            </div>

            <!-- Fecha -->
            <div class="col-12 col-md-6">
              <label class="form-label" for="fechaAnalisis">Fecha de Análisis</label>
              <input
                id="fechaAnalisis"
                type="date"
                class="form-control"
                v-model="analisisLocal.fecha_analisis"
                required
              />
            </div>

            <!-- Realizado por -->
            <div class="col-12 col-md-6">
              <label class="form-label" for="realizadoPor">Realizado por</label>
              <select
                id="realizadoPor"
                class="form-select"
                v-model="analisisLocal.realizado_por"
                required
              >
                <option disabled value="">Seleccione personal...</option>
                <option v-for="persona in personal" :key="persona.id" :value="persona.id">
                  {{ persona.nombre }} {{ persona.apellido }} - {{ persona.legajo }}
                </option>
              </select>
            </div>

            <!-- Método -->
            <div class="col-12 col-md-6">
              <label class="form-label" for="metodo">Método</label>
              <input
                id="metodo"
                type="text"
                class="form-control"
                v-model="analisisLocal.metodo"
                maxlength="100"
                placeholder="Ej: Cromatografía, Espectrometría..."
              />
            </div>

            <!-- Observaciones -->
            <div class="col-12">
              <label class="form-label" for="observaciones">Observaciones</label>
              <textarea
                id="observaciones"
                class="form-control"
                v-model="analisisLocal.observaciones"
                rows="3"
                maxlength="500"
                placeholder="Observaciones relevantes..."
              ></textarea>
            </div>

            <!-- Aprobado -->
            <div class="col-12 col-md-6 d-flex align-items-center">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="analisisLocal.aprobado"
                  id="checkAprobado"
                />
                <label class="form-check-label" for="checkAprobado">
                  ¿Aprobado?
                </label>
              </div>
            </div>

            <!-- Entidad -->
            <div class="col-12 col-md-6">
              <label class="form-label" for="entidad">Entidad</label>
              <div class="input-group mb-2">
                <input
                  id="entidad"
                  type="text"
                  class="form-control"
                  v-model="analisisLocal.entidad"
                  required
                  placeholder="Código de entidad"
                  aria-label="Código de entidad"
                />
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="fetchEntidadPorCodigo(analisisLocal.entidad)"
                  :disabled="!analisisLocal.entidad || loadingEntidad"
                >
                  <font-awesome-icon icon="search" class="me-1" />
                  <span v-if="!loadingEntidad">Buscar</span>
                  <span v-else class="spinner-border spinner-border-sm"></span>
                </button>
              </div>
              <div v-if="entidad" class="alert alert-success py-2 px-3 mt-2">
                <strong>Entidad encontrada:</strong><br>
                <span class="fw-bold">Código:</span> {{ entidad.entidadData.codigo }}<br>
                <span class="fw-bold">Nombre:</span> {{ entidad.entidadData.nombre }}<br>
              </div>
              <div v-else-if="analisisLocal.entidad && loadingEntidad" class="alert alert-info py-2 px-3 mt-2">
                Buscando entidad...
              </div>
              <div v-else-if="analisisLocal.entidad && !loadingEntidad" class="alert alert-warning py-2 px-3 mt-2">
                No se encontró la entidad. Indique un código de entidad válido.
              </div>
            </div>

            <!-- Botones -->
            <div class="col-12 d-flex flex-column flex-md-row justify-content-end gap-2 mt-3">
              <button type="submit" class="btn btn-success w-100 w-md-auto" :disabled="loadingSubmit">
                <font-awesome-icon icon="save" class="me-1" />
                <span v-if="!loadingSubmit">Guardar</span>
                <span v-else class="spinner-border spinner-border-sm"></span>
              </button>
              <button type="button" class="btn btn-secondary w-100 w-md-auto" @click="$emit('cancelar')" :disabled="loadingSubmit">
                <font-awesome-icon icon="times" class="me-1" />
                Cancelar
              </button>
            </div>

            <!-- Errores -->
            <div v-if="error" class="col-12">
              <div class="alert alert-danger mt-3">{{ error }}</div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'AnalisisForm',
  components: { FontAwesomeIcon },
  props: {
    analisis: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      analisisLocal: this.crearAnalisisBase(),
      tiposAnalisis: [],
      error: null,
      personal: [],
      clubActual: this.$store.getters.getClubActual,
      entidad: null,
      loadingEntidad: false,
      loadingSubmit: false
    }
  },
  watch: {
    analisis: {
      immediate: true,
      deep: true,
      handler(nuevo) {
        this.analisisLocal = nuevo && Object.keys(nuevo).length
          ? { ...nuevo }
          : this.crearAnalisisBase()
      }
    }
  },
  methods: {
    crearAnalisisBase() {
      // Obtener la fecha actual en formato yyyy-mm-dd
      const hoy = new Date();
      const yyyy = hoy.getFullYear();
      const mm = String(hoy.getMonth() + 1).padStart(2, '0');
      const dd = String(hoy.getDate()).padStart(2, '0');
      const fechaActual = `${yyyy}-${mm}-${dd}`;
      return {
        tipo_analisis_id: '',
        fecha_analisis: fechaActual,
        realizado_por: '',
        metodo: '',
        observaciones: '',
        aprobado: false,
        entidad: ''
      }
    },
    async fetchPersonal() {
      try {
        const response = await axios.get(
          "/api/personal",
          { params: { club_id: this.clubActual.id } }
        );
        this.personal = response.data;
      } catch (error) {
        console.error("Error al obtener el personal:", error);
      }
    },
    async fetchTiposAnalisis() {
      try {
        const res = await axios.get('/api/tipo_analisis')
        this.tiposAnalisis = res.data
      } catch (e) {
        this.error = 'Error al cargar tipos de análisis'
      }
    },
    async fetchEntidadPorCodigo(unCodigo) {
      if (!unCodigo) return;
      this.loadingEntidad = true;
      this.entidad = null;
      try {
        const res = await axios.get(`/api/analisis_entidad/${unCodigo}`)
        this.entidad = res.data
        //parsear fecha de creacion a formato dd/mm/yyyy
        this.entidad.entidadData.fecha_creacion = new Date(this.entidad.entidadData.fecha_creacion).toLocaleDateString('es-ES')
      } catch (e) {
        this.entidad = null;
      } finally {
        this.loadingEntidad = false;
      }
    },
    async submitForm() {
      this.loadingSubmit = true;
      try {
        if (!this.analisisLocal.tipo_analisis_id) {
          this.error = 'Debe seleccionar un tipo de análisis'
          this.loadingSubmit = false;
          return
        }
        if (this.analisisLocal.id) {
          await axios.put(`/api/analisis/${this.analisisLocal.id}`, this.analisisLocal)
        } else {
          await axios.post('/api/analisis', this.analisisLocal)
        }
        this.error = null
        this.$emit('guardado')
      } catch (e) {
        this.error = 'Error al guardar el análisis'
      } finally {
        this.loadingSubmit = false;
      }
    }
  },
  mounted() {
    this.fetchTiposAnalisis()
    this.fetchPersonal()
  }
}
</script>

<style scoped>
.card {
  max-width: 700px;
  margin: auto;
}
@media (max-width: 767px) {
  .card {
    max-width: 100%;
    margin: 0 0.5rem;
  }
}
</style>
